<template>
  <v-container class="py-8">
    <div class="mx-auto" style="max-width: 1300px;">
      <v-row class="gap-y-6" align="start" justify="center">
        <!-- Left: Avatar / Summary -->
        <v-col cols="12" md="4" lg="3">
          <v-card elevation="3" rounded="xl" class="text-center pa-6">
            <PlanBadgeAvatar :src="form.profile_picture || defaultAvatar" :size="140"
              :plan="currentPlanKey || (userData?.membership_plan ?? null)" class="mb-3" />

            <div class="text-h6 font-weight-semibold mb-1">{{ displayName }}</div>
            <div class="text-caption text-medium-emphasis">{{ form.email }}</div>

            <div class="mt-6 d-flex gap-3 justify-center">
              <v-btn variant="tonal" prepend-icon="mdi-camera" @click="uploadDialog = true">
                Change Photo
              </v-btn>
            </div>
          </v-card>
        </v-col>

        <!-- Upload dialog -->
        <v-dialog v-model="uploadDialog" max-width="480">
          <v-card rounded="xl">
            <v-card-title class="text-h6">Upload new photo</v-card-title>
            <v-card-text>
              <v-file-input v-model="selectedFile" accept="image/*" label="Choose an image" prepend-icon="mdi-image"
                show-size variant="outlined" rounded="lg" @update:modelValue="onFileChosen" />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="closeUpload">Cancel</v-btn>
              <v-btn color="primary" :loading="uploading" :disabled="!selectedFile" @click="uploadPhoto">
                Upload
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Right: Tabs (Profile / Membership) -->
        <v-col cols="12" md="8" lg="7">
          <v-card elevation="3" rounded="xl">
            <!-- Tabs -->
            <v-tabs v-model="tab" bg-color="transparent" color="primary" class="px-4 pt-3 mb-3">
              <v-tab value="profile" prepend-icon="mdi-account-circle">Profile</v-tab>
              <v-tab v-if="isAgent" value="membership" prepend-icon="mdi-crown-outline">Membership</v-tab>
            </v-tabs>
            <v-divider />

            <!-- Tab content -->
            <v-window v-model="tab">
              <!-- PROFILE TAB -->
              <v-window-item value="profile">
                <v-card-text class="pt-6">
                  <v-form ref="formRef" v-model="isValid">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="form.first_name" label="First name" variant="outlined" rounded="lg"
                          :rules="[r.required]" />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="form.middle_name" label="Middle name" variant="outlined" rounded="lg" />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field v-model="form.last_name" label="Last name" variant="outlined" rounded="lg"
                          :rules="[r.required]" />
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field v-model="form.email" disabled label="Email" type="email" variant="outlined"
                          rounded="lg" :rules="[r.required, r.email]" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="form.phone" label="Phone" variant="outlined" rounded="lg"
                          :rules="[r.required]" />
                      </v-col>

                      <v-col cols="12" md="6">
                        <div class="text-caption mb-1">Gender</div>
                        <v-btn-toggle v-model="form.gender" color="primary" mandatory divided class="w-100">
                          <v-btn value="Male" text="Male" />
                          <v-btn value="Female" text="Female" />
                        </v-btn-toggle>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field v-model="form.dob" label="Date of Birth" type="date"
                          variant="outlined" density="comfortable" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-combobox v-model="form.citizenship" :items="countryOptions" label="Citizenship"
                          variant="outlined" density="comfortable" clearable />
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-combobox v-model="form.language" :items="languageOptions" label="Language" variant="outlined"
                          density="comfortable" clearable />
                      </v-col>

                    </v-row>
                  </v-form>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                  <v-spacer />
                  <v-btn size="large" :loading="saving" color="primary" @click="saveProfile">Save Changes</v-btn>
                </v-card-actions>
              </v-window-item>

              <!-- MEMBERSHIP TAB -->
              <v-window-item value="membership">
                <v-card-text class="pt-6">
                  <div class="d-flex align-center justify-space-between mb-4">
                    <div>
                      <div class="text-h6 font-weight-semibold mb-1">Membership</div>
                      <div class="text-caption text-medium-emphasis">Manage or start a membership</div>
                    </div>

                    <v-chip :color="isMember ? 'green' : 'grey'" variant="tonal" class="font-weight-medium">
                      <v-icon start>{{ isMember ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                      {{ isMember ? (currentPlanKey ? currentPlanKey.toUpperCase() : 'Active') : 'Not Active' }}
                    </v-chip>
                  </div>

                  <v-alert :type="isMember ? 'success' : 'info'" variant="tonal" border="start" class="mb-5">
                    <template v-if="isMember">
                      Thanks for being a member! You can manage billing or change plans below.
                    </template>
                    <template v-else>
                      Choose a plan to get started. You can change plans anytime.
                    </template>
                  </v-alert>

                  <!-- 3-plan picker -->
                  <v-item-group v-model="selectedPlan" class="mb-6" mandatory>
                    <v-row>
                      <v-col v-for="p in plans" :key="p.key" cols="12" md="4">
                        <v-item :value="p.key" v-slot="{ isSelected, toggle }">
                          <v-card :elevation="isSelected ? 8 : 3" rounded="xl" class="h-100 d-flex flex-column"
                            :class="isSelected ? 'ring-2' : ''" @click="toggle">
                            <div class="d-flex justify-center mt-3">
                              <v-chip size="small" variant="tonal" color="primary">{{ p.badge }}</v-chip>
                            </div>
                            <v-card-title class="text-center">{{ p.title }}</v-card-title>
                            <div class="text-center text-medium-emphasis mb-2">{{ p.subtitle }}</div>
                            <div class="text-center text-h5 font-weight-bold mb-2">{{ p.priceLabel }}</div>

                            <v-divider class="my-2" />
                            <v-card-text class="pt-0">
                              <ul class="ma-0 pa-0" style="list-style:none">
                                <li v-for="f in p.features" :key="f" class="d-flex align-center mb-1">
                                  <v-icon size="16" class="mr-1">mdi-check</v-icon> <span class="text-body-2">{{ f
                                  }}</span>
                                </li>
                              </ul>
                            </v-card-text>

                            <v-spacer />
                            <v-card-actions class="px-4 pb-4">
                              <v-btn block :color="isSelected ? 'primary' : 'secondary'" variant="elevated"
                                :disabled="currentPlanKey === p.key">
                                {{ currentPlanKey === p.key ? 'Current Plan' : 'Select' }}
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </v-item>
                      </v-col>
                    </v-row>
                  </v-item-group>

                  <v-row>
                    <v-col cols="12" md="7">
                      <v-list density="comfortable">
                        <v-list-item>
                          <v-list-item-title>Selected</v-list-item-title>
                          <v-list-item-subtitle>{{ membershipPriceLabel }}</v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item v-if="overview?.subscription && isMember">
                          <v-list-item-title>Next renewal</v-list-item-title>
                          <v-list-item-subtitle>
                            {{ formatDateTime(overview.subscription.current_period_end) || '—' }}
                          </v-list-item-subtitle>
                        </v-list-item>
                        <v-list-item v-if="cardSummary && isMember">
                          <v-list-item-title>Card on file</v-list-item-title>
                          <v-list-item-subtitle>
                            {{ cardSummary.brand?.toUpperCase() }} •••• {{ cardSummary.last4 }}
                            ({{ cardSummary.exp_month }}/{{ String(cardSummary.exp_year).slice(-2) }})
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </v-col>

                    <v-col cols="12" md="5" class="d-flex">
                      <div class="d-flex flex-column align-end ga-3 w-100" style="max-width:320px; margin-left:auto;">
                        <!-- Start / Change plan -->
                        <v-btn v-if="!isMember || (isMember && selectedPlan !== currentPlanKey)" color="primary"
                          :height="44" density="comfortable" prepend-icon="mdi-crown-outline" @click="startOrChangePlan"
                          :disabled="selectedPlan === 'standard' && !isMember">
                          {{ !isMember ? 'Get Membership' : 'Change Plan' }}
                        </v-btn>

                        <!-- Activate free plan if not a member -->
                        <v-btn v-if="selectedPlan != 'standard'" variant="tonal" color="secondary" :height="44"
                          density="comfortable" prepend-icon="mdi-account-check" @click="activateFreePlan">
                          Activate Free Plan
                        </v-btn>

                        <!-- Existing management -->
                        <v-btn v-if="isMember" color="secondary" prepend-icon="mdi-credit-card-outline" :height="44"
                          density="comfortable" @click="openBillingManager">
                          Manage Billing
                        </v-btn>

                        <v-btn v-if="isMember" color="error" variant="tonal" prepend-icon="mdi-cancel" :height="44"
                          density="comfortable" @click="cancelDialog = true">
                          Cancel Subscription
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>

              </v-window-item>

              <!-- Manage Billing dialog (inline card update) -->
              <v-dialog v-model="billingDialog" max-width="560">
                <v-card rounded="xl">
                  <v-card-title class="text-h6">Manage Billing</v-card-title>
                  <v-divider />
                  <v-card-text>
                    <v-alert type="info" variant="tonal" border="start" class="mb-4">
                      Update the default card for your membership.
                    </v-alert>

                    <div class="mb-2" v-if="cardSummary">
                      <div class="text-caption text-medium-emphasis mb-1">Current card</div>
                      <div class="text-body-2">
                        {{ cardSummary.brand?.toUpperCase() }} •••• {{ cardSummary.last4 }}
                        ({{ cardSummary.exp_month }}/{{ String(cardSummary.exp_year).slice(-2) }})
                      </div>
                    </div>

                    <!-- Stripe Payment Element mounts here when "Update Card" is clicked -->
                    <div v-show="showPaymentForm" id="payment-element" class="pa-3 rounded-lg"
                      style="border:1px dashed var(--v-theme-surface-variant);"></div>
                  </v-card-text>

                  <v-card-actions class="px-6 pb-4">
                    <v-spacer />
                    <v-btn v-if="!showPaymentForm" variant="outlined" @click="billingDialog = false">Close</v-btn>
                    <v-btn v-else variant="text" @click="cancelUpdateCard" :disabled="savingCard">Cancel</v-btn>

                    <v-btn v-if="!showPaymentForm" color="primary" @click="beginUpdateCard" :loading="loadingElement">
                      Update Card
                    </v-btn>
                    <v-btn v-else color="primary" @click="saveNewCard" :loading="savingCard">Save Card</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <!-- Cancel dialog -->
              <v-dialog v-model="cancelDialog" max-width="520">
                <v-card rounded="xl">
                  <v-card-title class="text-h6">Cancel membership</v-card-title>
                  <v-divider />
                  <v-card-text>
                    <v-alert type="warning" variant="tonal" icon="mdi-alert" border="start" class="mb-4">
                      You can cancel at the end of the current period (recommended) or immediately.
                    </v-alert>

                    <v-radio-group v-model="cancelMode" density="comfortable">
                      <v-radio :value="'period_end'" label="Cancel at period end (keep access until renewal)" />
                      <v-radio :value="'immediate'" label="Cancel immediately (access ends now)" />
                    </v-radio-group>

                    <div v-if="overview?.subscription?.cancel_at_period_end"
                      class="text-caption text-medium-emphasis mt-2">
                      Already set to cancel on
                      <strong>
                        {{ formatDateTime(overview.subscription.cancel_at) ||
                          formatDateTime(overview.subscription.current_period_end)
                        }}
                      </strong>.
                    </div>
                  </v-card-text>
                  <v-card-actions class="px-6 pb-4">
                    <v-spacer />
                    <v-btn variant="text" @click="cancelDialog = false" :disabled="cancelling">Close</v-btn>
                    <v-btn color="error" @click="confirmCancel" :loading="cancelling">Confirm Cancel</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <!-- Inline Checkout Dialog -->
              <v-dialog v-model="inlineDialog" max-width="560">
                <v-card rounded="xl">
                  <v-card-title class="text-h6">Complete membership purchase</v-card-title>
                  <v-divider />
                  <v-card-text>
                    <v-alert type="info" variant="tonal" border="start" class="mb-4">
                      <div class="mb-1">
                        Enter your payment details to start your membership.
                      </div>
                      <div class="text-body-2 mt-1">
                        <strong>Billing:</strong>
                        This is a <strong>monthly subscription</strong>. You’ll be charged
                        <strong>{{ membershipPriceLabel || '$XX / month' }}</strong> today and then every month until
                        you cancel. You can cancel any time from your account.
                      </div>
                    </v-alert>

                    <!-- Loading while Stripe sets up -->
                    <div v-if="loadingStripe || !paymentElementReady"
                      class="d-flex align-center justify-center rounded-lg"
                      style="min-height: 160px; border:1px dashed var(--v-theme-surface-variant);">
                      <v-progress-circular indeterminate size="28" class="mr-3" />
                      <span class="text-medium-emphasis">Loading secure payment form…</span>
                    </div>

                    <!-- Mount target: ref + key ensures fresh node each open -->
                    <div v-show="paymentElementReady" :key="mountKey" ref="paymentContainer" class="pa-3 rounded-lg"
                      style="border:1px dashed var(--v-theme-surface-variant);"></div>

                    <div v-if="inlineError" class="text-error text-body-2 mt-3">
                      {{ inlineError }}
                    </div>
                  </v-card-text>
                  <v-card-actions class="px-6 pb-4">
                    <v-spacer />
                    <v-btn variant="text" :disabled="inlineProcessing" @click="closeInlineCheckout">Cancel</v-btn>
                    <v-btn color="primary" :loading="inlineProcessing" :disabled="!paymentElementReady"
                      @click="confirmInlineCheckout">
                      Start Membership
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-window>
          </v-card>
        </v-col>
      </v-row>

      <!-- Snackbars -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" timeout="3000">
        {{ snackbar.msg }}
      </v-snackbar>
    </div>
  </v-container>
</template>

<script setup>
import { nextTick, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { supabase } from '@/supabaseClient'
import { loadStripe } from '@stripe/stripe-js'
import PlanBadgeAvatar from '@/components/PlanBadgeAvatar.vue'

/** ---------- ENV (prefer .env; fallback to hardcoded dev values) ---------- */
const PUBLISHABLE_KEY = 'pk_test_51RbWcvCIDggG1dGOHBABvIbCBK6Rpd5efaZcpXTCKjPpbJ7RL2x2RV43rT42ODPpGuMB5Ke54RpMwS04UaJTIqZP00QMwS8Hsn'
const PRICES = {
  standard: { priceId: null, label: 'Standard — $0 / month', amount: 0 },
  enhanced: { priceId: 'price_1S2eG5CIDggG1dGOQ8wZ6Ktt', label: 'Enhanced — $100 / month', amount: 10000 },
  premium: { priceId: 'price_xxxPremium', label: 'Premium — $175 / month', amount: 17500 },
}

/** ---------- STORE / USER ---------- */
const store = useStore()
const userData = computed(() => store.state.user)
const isAgent = computed(() => !!userData.value?.is_agent)

/** ---------- UI STATE ---------- */
const defaultAvatar = 'https://placehold.co/200x200?text=Avatar'
const formRef = ref(null)
const isValid = ref(false)
const saving = ref(false)
const snackbar = ref({ show: false, color: 'success', msg: '' })

const uploadDialog = ref(false)
const selectedFile = ref(null)
const uploading = ref(false)
const tab = ref('profile') // 'profile' | 'membership'

/** ---------- PROFILE FORM ---------- */
const form = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  gender: '',
  dob: null,
  citizenship: '',
  language: '',
  profile_picture: ''
})
const displayName = computed(() => [form.value.first_name, form.value.last_name].filter(Boolean).join(' ') || '—')
const r = {
  required: v => (!!v && String(v).trim().length > 0) || 'Required',
  email: v => (!v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) || 'Invalid email'
}

/** ---------- MEMBERSHIP (OVERVIEW + BILLING) ---------- */
const overview = ref(null) // from billing_overview

const billingDialog = ref(false)
const showPaymentForm = ref(false)
const loadingElement = ref(false)
const savingCard = ref(false)

const cancelDialog = ref(false)
const cancelMode = ref('period_end') // 'period_end' | 'immediate'
const cancelling = ref(false)

const inlineDialog = ref(false)
const inlineProcessing = ref(false)
const inlineError = ref('')
const loadingStripe = ref(false)
const paymentElementReady = ref(false)

/* Inline checkout mount target */
const paymentContainer = ref(null)
const mountKey = ref(0)

/** ---------- PLAN SELECTION ---------- */
const selectedPlan = ref('standard') // default pick for new purchase
const plans = [
  { key: 'standard', title: 'Standard', subtitle: 'Free basics', badge: 'PERSONAL', features: ['Marketplace access', 'List inventory'], priceLabel: 'Free' },
  { key: 'enhanced', title: 'Enhanced', subtitle: 'Grow faster', badge: 'POPULAR', features: ['Placement boost', 'Basic analytics'], priceLabel: '$100/mo' },
  { key: 'premium', title: 'Premium', subtitle: 'Best visibility', badge: 'BEST VALUE', features: ['Promo/month', 'Premium badge'], priceLabel: '$175/mo' },
]

const countryOptions = [
  'United States', 'Canada', 'Mexico', 'United Kingdom', 'Australia',
  'Germany', 'France', 'Italy', 'Spain', 'Brazil', 'India', 'China', 'Japan'
]
const languageOptions = [
  'English', 'Spanish', 'French', 'German', 'Italian',
  'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi'
]

// figure out the active plan from the subscription (if any)
const currentPlanKey = computed(() => {
  return userData.value?.membership_plan || 'standard'
})

const membershipPriceLabel = computed(() => {
  // show current plan label if subscribed; otherwise show for selected plan
  if (isMember.value && currentPlanKey.value) return PRICES[currentPlanKey.value]?.label?.replace(/ — .+$/, ' — current')
  const key = selectedPlan.value
  return PRICES[key]?.label || '$— / month'
})

function syncSelectedFromState() {
  // If subscriber → mirror Stripe plan
  if (isMember.value && currentPlanKey.value) {
    selectedPlan.value = currentPlanKey.value
    return
  }

  // Not a subscriber → prefer what's in your users table if present,
  // otherwise keep whatever the user last clicked, or default 'standard'
  const fallback = userData.value?.membership_plan || 'standard'
  selectedPlan.value = selectedPlan.value || fallback
}

function toDateOnly(val) {
  if (!val) return ''
  if (typeof val === 'string') return val.slice(0, 10) // handles 'YYYY-MM-DD...' or ISO
  const d = new Date(val)
  if (isNaN(d)) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** ---------- DERIVED ---------- */
const isMember = computed(() => {
  const s = overview.value?.subscription?.status
  if (s) return ['active', 'trialing', 'past_due'].includes(s)
  return !!userData.value?.paid_user
})
const cardSummary = computed(() => overview.value?.card || null)

// run right away and whenever overview/membership flips
watch([overview, isMember, currentPlanKey], () => {
  syncSelectedFromState()
}, { immediate: true })

// also when the user opens the Membership tab
watch(tab, (t) => {
  if (t === 'membership') syncSelectedFromState()
})

/** ---------- STRIPE (shared instance) ---------- */
let stripe = null

/* UPDATE CARD (billing manager) instances */
let updateElements = null
let updatePaymentElement = null
let setupClientSecret = null

/* SUBSCRIPTION CHECKOUT (inline) instances */
let checkoutElements = null
let checkoutPaymentEl = null
let currentClientSecret = ''

/** ---------- HELPERS ---------- */
function toast(msg, type = 'success') {
  snackbar.value = { show: true, color: type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'success', msg }
}
function formatDateTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return isNaN(d) ? '' : d.toLocaleString()
}
async function poll(fn, { interval = 1200, timeout = 20000 } = {}) {
  const start = Date.now()
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const ok = await fn()
    if (ok) return true
    if (Date.now() - start > timeout) return false
    await new Promise(r => setTimeout(r, interval))
  }
}

/** ---------- LOAD PROFILE ---------- */
async function loadProfile() {
  const u = userData.value
  if (!u) return
  console.log(u)
  form.value = { ...form.value, ...pickProfile(u) }
}
function pickProfile(src = {}) {
  // prefer any non-empty URL; only fall back if truly missing
  const pic =
    src.profile_picture && src.profile_picture !== 'https://placehold.co/100'
      ? src.profile_picture
      : defaultAvatar

  return {
    // names + contact
    first_name: src.first_name || '',
    middle_name: src.middle_name || '',
    last_name: src.last_name || '',
    email: src.email || '',
    phone: src.phone || '',

    // extras shown in your form
    gender: src.gender || '',
    dob: toDateOnly(src.dob),            // <-- normalized for <input type="date">
    citizenship: src.citizenship || '',
    language: src.language || '',

    // misc
    bio: src.bio || '',
    profile_picture: pic,
  }
}
watch(userData, loadProfile, { immediate: true })
onMounted(loadProfile)

/** ---------- PROFILE ACTIONS ---------- */
function onFileChosen() {
  const file = selectedFile.value?.[0] || selectedFile.value
  if (!file) return
  const url = URL.createObjectURL(file)
  form.value.profile_picture = url
}
function closeUpload() {
  uploadDialog.value = false
  selectedFile.value = null
}
async function uploadPhoto() {
  const file = selectedFile.value?.[0] || selectedFile.value
  if (!file) return toast('Pick a file first', 'warning')
  const uid = userData.value?.uid || userData.value?.id
  if (!uid) return toast('Not authenticated', 'error')

  uploading.value = true
  try {
    const path = `${uid}/${Date.now()}_${file.name}`
    const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { cacheControl: '3600', upsert: true })
    if (upErr) throw upErr
    const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path)
    const publicUrl = pub?.publicUrl
    if (!publicUrl) throw new Error('No public URL returned')

    const { error: dbErr } = await supabase.from('users').update({ profile_picture: publicUrl }).eq('uid', uid)
    if (dbErr) throw dbErr

    form.value.profile_picture = publicUrl
    toast('Profile photo updated!')
    closeUpload()
  } catch (e) {
    console.error(e)
    toast(`Upload failed: ${e.message || e}`, 'error')
  } finally {
    uploading.value = false
  }
}
async function saveProfile() {
  const ok = await formRef.value?.validate()
  if (!ok?.valid) return
  const uid = userData.value?.uid || userData.value?.id
  if (!uid) return toast('Not authenticated', 'error')

  try {
    saving.value = true
    const payload = {
      first_name: form.value.first_name,
      middle_name: form.value.middle_name,
      last_name: form.value.last_name,
      email: form.value.email,
      phone: form.value.phone,
      gender: form.value.gender,
      dob: form.value.dob ? new Date(form.value.dob).toISOString() : null, // store ISO if you prefer
      citizenship: form.value.citizenship,
      language: form.value.language,
      profile_picture: form.value.profile_picture,
    }
    const { error } = await supabase.from('users').update(payload).eq('uid', uid)
    if (error) throw error
    toast('Profile saved!')
  } catch (e) {
    toast(`Save failed: ${e.message || e}`, 'error')
  } finally {
    saving.value = false
  }
}

/** ---------- MEMBERSHIP ACTIONS ---------- */
async function loadOverview() {
  const { data, error } = await supabase.functions.invoke('billing_overview')
  if (error) {
    console.error(error)
    toast('Could not load billing overview', 'error')
    return
  }
  overview.value = data
}

async function openBillingManager() {
  await loadOverview()
  billingDialog.value = true
  showPaymentForm.value = false
}

async function beginUpdateCard() {
  try {
    loadingElement.value = true
    if (!stripe) {
      if (!PUBLISHABLE_KEY) throw new Error('Missing publishable key')
      stripe = await loadStripe(PUBLISHABLE_KEY)
    }
    const { data, error } = await supabase.functions.invoke('start_pm_update')
    if (error) throw error
    setupClientSecret = data?.client_secret
    if (!setupClientSecret) throw new Error('No client_secret')

    // Destroy any previous update flow elements
    try { updatePaymentElement?.unmount?.() } catch { console.error('error') }
    try { updateElements?.destroy?.() } catch { console.error('error') }

    updateElements = stripe.elements({ clientSecret: setupClientSecret })
    updatePaymentElement = updateElements.create('payment')
    updatePaymentElement.mount('#payment-element')

    showPaymentForm.value = true
  } catch (e) {
    console.error(e)
    toast('Could not start card update', 'error')
  } finally {
    loadingElement.value = false
  }
}
function cancelUpdateCard() {
  showPaymentForm.value = false
  try { updatePaymentElement?.unmount?.() } catch { console.error('error') }
  try { updateElements?.destroy?.() } catch { console.error('error') }
  updatePaymentElement = null
  updateElements = null
  setupClientSecret = null
}
async function saveNewCard() {
  if (!stripe || !updateElements || !setupClientSecret) return
  savingCard.value = true
  try {
    const { error, setupIntent } = await stripe.confirmSetup({
      elements: updateElements,
      clientSecret: setupClientSecret,
      redirect: 'if_required'
    })
    if (error) throw error
    const pmId = setupIntent?.payment_method
    if (!pmId) throw new Error('No payment_method returned')

    const { error: setErr } = await supabase.functions.invoke('set_default_pm', { body: { payment_method_id: pmId } })
    if (setErr) throw setErr

    toast('Card updated')
    cancelUpdateCard()
    await loadOverview()
  } catch (e) {
    console.error(e)
    toast('Failed to update card', 'error')
  } finally {
    savingCard.value = false
  }
}
async function confirmCancel() {
  if (!userData.value?.uid) return toast('Not authenticated', 'error')
  try {
    cancelling.value = true
    const { data, error } = await supabase.functions.invoke('cancel_subscription', {
      body: { immediate: cancelMode.value === 'immediate' }
    })
    if (error) throw error
    await loadOverview?.()
    if (data?.canceled) toast('Subscription canceled immediately.')
    else if (data?.canceled_at_period_end || data?.ok) toast('Subscription will cancel at period end.')
    else toast('No active subscription found.')
    cancelDialog.value = false
  } catch (e) {
    console.error(e)
    toast(`Cancel failed: ${e.message || e}`, 'error')
  } finally {
    cancelling.value = false
  }
}

async function startOrChangePlan() {
  const key = selectedPlan.value
  const priceId = PRICES[key]?.priceId

  if (key === 'standard') {
    // Free plan via DB flag (no Stripe)
    if (!isMember.value) return activateFreePlan()
    return toast('You are already a member; switch to Standard by canceling paid plan first.', 'warning')
  }

  if (!priceId) return toast('Price not configured for this plan.', 'error')

  // Reuse your existing inline checkout flow, but pass the chosen priceId
  inlineError.value = ''
  loadingStripe.value = true
  paymentElementReady.value = false
  inlineDialog.value = true

  try {
    await nextTick()

    const { data, error } = await supabase.functions.invoke('create_subscription_intent', {
      body: { priceId }   // <-- use selected plan
    })
    if (error) throw error
    if (!data?.client_secret) throw new Error('No client secret returned')
    currentClientSecret = data.client_secret

    mountKey.value++
    await nextTick()
    if (!paymentContainer.value) throw new Error('Mount container not found')

    if (!stripe) {
      if (!PUBLISHABLE_KEY) throw new Error('Missing publishable key')
      stripe = await loadStripe(PUBLISHABLE_KEY)
    }

    checkoutElements = stripe.elements({ clientSecret: currentClientSecret, appearance: { theme: 'flat' } })
    checkoutPaymentEl = checkoutElements.create('payment')
    checkoutPaymentEl.on('ready', () => {
      paymentElementReady.value = true
      loadingStripe.value = false
    })
    checkoutPaymentEl.mount(paymentContainer.value)
  } catch (e) {
    inlineError.value = e?.message || String(e)
    loadingStripe.value = false
  }
}

async function activateFreePlan() {
  // Mark as non-paid plan in your own DB (no Stripe)
  const uid = userData.value?.uid || userData.value?.id
  if (!uid) return toast('Not authenticated', 'error')
  try {
    const { error } = await supabase.from('users').update({ paid_user: false }).eq('uid', uid)
    if (error) throw error
    toast('Free plan activated.')
    // client-side (you already have something similar)
    await supabase
      .from('users')
      .update({
        membership_plan: 'standard',
        membership_status: null,
        stripe_subscription_id: null,
        stripe_price_id: null,
        plan_started_at: new Date().toISOString(),
        plan_renews_at: null,
        cancel_at_period_end: null,
        paid_user: false
      })
      .eq('uid', uid)

    selectedPlan.value = 'standard'
    await loadOverview() // ensures UI reflects non-paid state
  } catch (e) {
    toast(`Could not activate free plan: ${e.message || e}`, 'error')
  }
}


/** ---------- INLINE SUBSCRIPTION CHECKOUT ---------- */
function closeInlineCheckout() {
  try { checkoutPaymentEl?.unmount?.() } catch { console.error('error') }
  try { checkoutElements?.destroy?.() } catch { console.error('error') }
  checkoutElements = null
  checkoutPaymentEl = null
  currentClientSecret = ''
  inlineDialog.value = false
  inlineProcessing.value = false
  inlineError.value = ''
  loadingStripe.value = false
  paymentElementReady.value = false
}

// confirm the first invoice PaymentIntent (handles 3DS in-place)
async function confirmInlineCheckout() {
  if (!stripe || !checkoutElements || !currentClientSecret) return
  inlineProcessing.value = true
  inlineError.value = ''

  try {
    // 1) Validate fields first
    const { error: submitError } = await checkoutElements.submit()
    if (submitError) {
      inlineError.value = submitError.message || 'Please check your payment details.'
      return
    }

    // 2) Confirm payment for the first invoice
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements: checkoutElements,
      clientSecret: currentClientSecret,
      redirect: 'if_required',
    })

    if (error) {
      inlineError.value = error.message || 'Payment failed. Try a different card.'
      return
    }

    // 3) If Stripe returned a PI, decide based on status
    const status = paymentIntent?.status

    if (status === 'succeeded' || status === 'requires_capture') {
      // ✅ Close FIRST so any follow-up doesn’t trap the dialog open
      toast('Payment succeeded!')
      closeInlineCheckout()

      // Refresh overview in the background (don’t block the UI)
      loadOverview().catch(() => {
        // non-fatal; UI is already closed
        console.warn('loadOverview failed after success')
      })
      return
    }

    // Likely "processing" — close optimistically and poll in background
    toast('Payment processing… this can take a few seconds.')
    closeInlineCheckout()

    // Background poll to flip UI when webhook finishes
    poll(async () => {
      await loadOverview()
      return !!isMember.value
    }, { interval: 1200, timeout: 20000 }).then((ok) => {
      if (!ok) toast('Payment received. Your membership will activate shortly.')
    }).catch(() => { })
  } catch (e) {
    inlineError.value = e?.message || String(e)
  } finally {
    inlineProcessing.value = false
  }
}


/** ---------- LIFECYCLE ---------- */
onMounted(async () => {
  if (tab.value === 'membership') await loadOverview()
})
watch(tab, async t => {
  if (t === 'membership') await loadOverview()
})
watch(isMember, (val) => {
  // If webhook flips membership while dialog is open, auto-close
  if (val && inlineDialog.value) {
    closeInlineCheckout()
    toast('Membership activated!')
  }
})
onBeforeUnmount(() => {
  try { updatePaymentElement?.unmount?.() } catch { console.error('error') }
  try { updateElements?.destroy?.() } catch { console.error('error') }
  try { checkoutPaymentEl?.unmount?.() } catch { console.error('error') }
  try { checkoutElements?.destroy?.() } catch { console.error('error') }
})
</script>


<style scoped>
.v-card {
  backdrop-filter: saturate(1.1);
}
</style>
